import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  collection, query, where, onSnapshot, doc, Timestamp,
} from 'firebase/firestore';
import { setDoc, updateDoc, deleteDoc } from '../config/escrituras';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import { usePerfil } from './PerfilContext';
import {
  PerfilFilamento, PerfilImpresora, Presupuesto3D, ParametrosPedido3D, ResultadoPedido3D,
} from '../types';

/**
 * Datos del módulo Taller 3D. Vive aparte de DataContext porque es un vertical
 * distinto: cuando lleguen Clientes / Pedidos / Facturas, cada uno traerá su
 * propio contexto y no habrá que tocar el de finanzas.
 */
interface Taller3DContextType {
  perfilesFilamento: PerfilFilamento[];
  perfilesImpresora: PerfilImpresora[];
  presupuestos3D: Presupuesto3D[];
  loading3D: boolean;

  guardarPerfilFilamento: (p: Omit<PerfilFilamento, 'id' | 'usuarioId' | 'createdAt'>) => Promise<void>;
  eliminarPerfilFilamento: (id: string) => Promise<void>;

  guardarPerfilImpresora: (p: Omit<PerfilImpresora, 'id' | 'usuarioId' | 'createdAt'>) => Promise<void>;
  eliminarPerfilImpresora: (id: string) => Promise<void>;

  guardarPresupuesto3D: (params: {
    id?: string;
    nombre: string;
    parametros: ParametrosPedido3D;
    resultado: ResultadoPedido3D;
    clienteId?: string;
    notas?: string;
  }) => Promise<string>;
  actualizarPresupuesto3D: (id: string, datos: Partial<Presupuesto3D>) => Promise<void>;
  eliminarPresupuesto3D: (id: string) => Promise<void>;
}

const Taller3DContext = createContext<Taller3DContextType | undefined>(undefined);

const sinUndefined = (o: Record<string, any>) =>
  Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined));

export const Taller3DProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  // Solo se escucha con el módulo activado: sin él, las reglas rechazan la
  // consulta y el listener muere; al activarlo, el efecto vuelve a suscribirse.
  const { tieneModulo } = usePerfil();
  const conModulo = tieneModulo('taller3d');

  const [perfilesFilamento, setPerfilesFilamento] = useState<PerfilFilamento[]>([]);
  const [perfilesImpresora, setPerfilesImpresora] = useState<PerfilImpresora[]>([]);
  const [presupuestos3D, setPresupuestos3D] = useState<Presupuesto3D[]>([]);
  const [loading3D, setLoading3D] = useState(true);

  useEffect(() => {
    if (!user || !conModulo) {
      setPerfilesFilamento([]);
      setPerfilesImpresora([]);
      setPresupuestos3D([]);
      setLoading3D(false);
      return;
    }

    setLoading3D(true);
    const q = (col: string) => query(collection(db, col), where('usuarioId', '==', user.uid));

    const unsubFil = onSnapshot(q('perfilesFilamento'), snap => {
      setPerfilesFilamento(snap.docs.map(d => ({
        ...d.data(),
        id: d.id,
        createdAt: d.data().createdAt?.toDate() || new Date(),
      })) as PerfilFilamento[]);
    });

    const unsubPrn = onSnapshot(q('perfilesImpresora'), snap => {
      setPerfilesImpresora(snap.docs.map(d => ({
        ...d.data(),
        id: d.id,
        createdAt: d.data().createdAt?.toDate() || new Date(),
      })) as PerfilImpresora[]);
    });

    const unsubPre = onSnapshot(q('presupuestos3d'), snap => {
      const lista = snap.docs.map(d => ({
        ...d.data(),
        id: d.id,
        createdAt: d.data().createdAt?.toDate() || new Date(),
        updatedAt: d.data().updatedAt?.toDate() || d.data().createdAt?.toDate() || new Date(),
        clienteId: d.data().clienteId || undefined,   // null (desasignado) -> undefined
      })) as Presupuesto3D[];
      lista.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());
      setPresupuestos3D(lista);
      setLoading3D(false);
    });

    return () => { unsubFil(); unsubPrn(); unsubPre(); };
  }, [user, conModulo]);

  // ── Perfiles de filamento ──────────────────────────────────────────────────
  const guardarPerfilFilamento: Taller3DContextType['guardarPerfilFilamento'] = async p => {
    if (!user) return;
    // Un mismo nombre sobrescribe el perfil existente en vez de duplicarlo
    const existente = perfilesFilamento.find(x => x.nombre === p.nombre);
    if (existente) {
      await updateDoc(doc(db, 'perfilesFilamento', existente.id), sinUndefined({ ...p }));
      return;
    }
    await setDoc(doc(collection(db, 'perfilesFilamento')), sinUndefined({
      ...p, usuarioId: user.uid, createdAt: Timestamp.now(),
    }));
  };

  const eliminarPerfilFilamento = async (id: string) => {
    await deleteDoc(doc(db, 'perfilesFilamento', id));
  };

  // ── Perfiles de impresora ──────────────────────────────────────────────────
  const guardarPerfilImpresora: Taller3DContextType['guardarPerfilImpresora'] = async p => {
    if (!user) return;
    const existente = perfilesImpresora.find(x => x.nombre === p.nombre);
    if (existente) {
      await updateDoc(doc(db, 'perfilesImpresora', existente.id), sinUndefined({ ...p }));
      return;
    }
    await setDoc(doc(collection(db, 'perfilesImpresora')), sinUndefined({
      ...p, usuarioId: user.uid, createdAt: Timestamp.now(),
    }));
  };

  const eliminarPerfilImpresora = async (id: string) => {
    await deleteDoc(doc(db, 'perfilesImpresora', id));
  };

  // ── Presupuestos ───────────────────────────────────────────────────────────
  const guardarPresupuesto3D: Taller3DContextType['guardarPresupuesto3D'] = async ({
    id, nombre, parametros, resultado, clienteId, notas,
  }) => {
    if (!user) throw new Error('Sesión no iniciada');

    const resumen = {
      lineas: parametros.lineas.length,
      unidades: resultado.unidadesTotales,
      costeTotal: resultado.costeTotal,
      precioTotalConIva: resultado.precioTotalConIva,
      beneficioTotal: resultado.beneficioTotal,
    };

    if (id) {
      await updateDoc(doc(db, 'presupuestos3d', id), {
        ...sinUndefined({ nombre, parametros, resumen, notas }),
        // Va explicito, no por sinUndefined: al quitar el cliente hay que
        // escribir null para borrar el campo, no omitirlo.
        clienteId: clienteId ?? null,
        updatedAt: Timestamp.now(),
      });
      return id;
    }

    const ref = doc(collection(db, 'presupuestos3d'));
    await setDoc(ref, sinUndefined({
      nombre,
      parametros,
      resumen,
      clienteId,
      notas,
      estado: 'borrador',
      usuarioId: user.uid,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    }));
    return ref.id;
  };

  const actualizarPresupuesto3D = async (id: string, datos: Partial<Presupuesto3D>) => {
    await updateDoc(doc(db, 'presupuestos3d', id), sinUndefined({
      ...datos, updatedAt: Timestamp.now(),
    }));
  };

  const eliminarPresupuesto3D = async (id: string) => {
    await deleteDoc(doc(db, 'presupuestos3d', id));
  };

  return (
    <Taller3DContext.Provider value={{
      perfilesFilamento, perfilesImpresora, presupuestos3D, loading3D,
      guardarPerfilFilamento, eliminarPerfilFilamento,
      guardarPerfilImpresora, eliminarPerfilImpresora,
      guardarPresupuesto3D, actualizarPresupuesto3D, eliminarPresupuesto3D,
    }}>
      {children}
    </Taller3DContext.Provider>
  );
};

export const useTaller3D = () => {
  const ctx = useContext(Taller3DContext);
  if (!ctx) throw new Error('useTaller3D debe usarse dentro de Taller3DProvider');
  return ctx;
};
